/**
 * Lead ropes and fence knots. Each lead is 24 segments of two crossed
 * 0.05-wide ribbons (alternating two browns like vanilla) hanging in a parabola
 * from the holder (the player's right hand, a knot or another mob) to the
 * leashed mob's neck. Knots are vanilla's LeashKnotModel (6x8x6 box on
 * entity/lead_knot/lead_knot.png, 32x32) around the fence post.
 * Positions interpolate between ticks with `alpha`.
 */

import * as THREE from 'three';
import { boxGeometry } from './firstPerson';
import type { Mob } from '../entities/mob';
import type { LeashHolder } from '../entities/leash';

const SEGS = 24;
const MAX_LINKS = 64;
const VERTS_PER_LINK = SEGS * 8;
/** Half-thickness of the rope. */
const HALF = 0.025;
/** Vertex colours are linear in three.js; vanilla's browns are sRGB-ish. */
const LIN = (c: number): number => Math.pow(c, 2.2);

export interface LeashDraw {
  entityById(id: number): Mob | undefined;
  light(x: number, y: number, z: number): number;
  /** The rope anchor at the player's hand (already interpolated). */
  player(): [number, number, number];
}

/** Sagging rope between two points: the y offset below the straight line at fraction `t`. */
export function ropeSag(len: number, t: number): number {
  // slack shrinks as the rope gets taut (6+ blocks is nearly straight)
  const slack = Math.max(0.04, 0.5 * (1 - len / 10)) * Math.min(1, 0.4 + len * 0.25);
  return slack * 4 * t * (1 - t);
}

export class LeashRenderer {
  private readonly geo = new THREE.BufferGeometry();
  private readonly pos = new Float32Array(MAX_LINKS * VERTS_PER_LINK * 3);
  private readonly col = new Float32Array(MAX_LINKS * VERTS_PER_LINK * 3);
  private readonly mesh: THREE.Mesh;
  private readonly knotGeo: THREE.BufferGeometry;
  private readonly knotTex: THREE.Texture;
  private readonly knots: THREE.Mesh[] = [];
  private readonly group = new THREE.Group();
  private readonly tmpA: [number, number, number] = [0, 0, 0];

  constructor(scene: THREE.Scene) {
    const idx = new Uint32Array(MAX_LINKS * SEGS * 2 * 6);
    let k = 0;
    for (let q = 0; q < MAX_LINKS * SEGS * 2; q++) {
      const v = q * 4;
      idx[k++] = v; idx[k++] = v + 1; idx[k++] = v + 2;
      idx[k++] = v; idx[k++] = v + 2; idx[k++] = v + 3;
    }
    this.geo.setAttribute('position', new THREE.BufferAttribute(this.pos, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setAttribute('color', new THREE.BufferAttribute(this.col, 3).setUsage(THREE.DynamicDrawUsage));
    this.geo.setIndex(new THREE.BufferAttribute(idx, 1));
    this.geo.setDrawRange(0, 0);
    this.mesh = new THREE.Mesh(this.geo, new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.DoubleSide }));
    this.mesh.frustumCulled = false;
    this.group.add(this.mesh);

    this.knotTex = new THREE.TextureLoader().load('mc/entity/lead_knot/lead_knot.png');
    this.knotTex.magFilter = this.knotTex.minFilter = THREE.NearestFilter;
    this.knotTex.colorSpace = THREE.SRGBColorSpace;
    this.knotTex.generateMipmaps = false;
    // box -3,-8,-3 6x8x6 at uv (0, 0); the model is drawn upside down, so flip y to put the 8 px above the origin
    this.knotGeo = boxGeometry(0, 0, -3, -8, -3, 6, 8, 6, 0, 32, 32);
    this.knotGeo.scale(1, -1, 1);
    scene.add(this.group);
  }

  private mobPoint(m: Mob, alpha: number, up: number, fwd: number, out: [number, number, number]): void {
    const x = m.px + (m.x - m.px) * alpha, y = m.py + (m.y - m.py) * alpha, z = m.pz + (m.z - m.pz) * alpha;
    const yaw = m.yaw;
    out[0] = x - Math.sin(yaw) * fwd; out[1] = y + m.height * up; out[2] = z - Math.cos(yaw) * fwd;
  }

  update(links: ReadonlyArray<{ mob: Mob; holder: LeashHolder }>, knots: ReadonlyArray<{ x: number; y: number; z: number }>, alpha: number, draw: LeashDraw): void {
    const pos = this.pos, col = this.col;
    let v = 0;
    const a = this.tmpA;
    const b: [number, number, number] = [0, 0, 0];
    let n = 0;
    for (const { mob, holder } of links) {
      if (n >= MAX_LINKS) break;
      // rope runs from the holder (a) to the mob's neck (b)
      switch (holder.kind) {
        case 'player': { const p = draw.player(); a[0] = p[0]; a[1] = p[1]; a[2] = p[2]; break; }
        case 'knot': a[0] = holder.x + 0.5; a[1] = holder.y + 0.625; a[2] = holder.z + 0.5; break;
        case 'mob': {
          const h = draw.entityById(holder.id);
          if (!h) continue;
          this.mobPoint(h, alpha, 0.6, 0, a);
          break;
        }
      }
      this.mobPoint(mob, alpha, 0.72, mob.width * 0.35, b);
      const dx = b[0] - a[0], dy = b[1] - a[1], dz = b[2] - a[2];
      const len = Math.hypot(dx, dy, dz);
      const hl = Math.hypot(dx, dz) || 1;
      const px = (-dz / hl) * HALF, pz = (dx / hl) * HALF;
      const lit = 0.3 + 0.7 * Math.max(0, Math.min(1, draw.light((a[0] + b[0]) / 2, (a[1] + b[1]) / 2, (a[2] + b[2]) / 2)));
      for (let i = 0; i < SEGS; i++) {
        const shade = (i & 1 ? 0.7 : 1) * lit;
        const r = LIN(0.5 * shade), g = LIN(0.4 * shade), bl = LIN(0.3 * shade);
        const t0 = i / SEGS, t1 = (i + 1) / SEGS;
        const x0 = a[0] + dx * t0, y0 = a[1] + dy * t0 - ropeSag(len, t0), z0 = a[2] + dz * t0;
        const x1 = a[0] + dx * t1, y1 = a[1] + dy * t1 - ropeSag(len, t1), z1 = a[2] + dz * t1;
        for (let s = 0; s < 2; s++) {
          const ox = s === 0 ? px : 0, oy = s === 0 ? 0 : HALF, oz = s === 0 ? pz : 0;
          const q = [
            x0 - ox, y0 - oy, z0 - oz, x1 - ox, y1 - oy, z1 - oz,
            x1 + ox, y1 + oy, z1 + oz, x0 + ox, y0 + oy, z0 + oz,
          ];
          for (let c = 0; c < 4; c++) {
            const o = (v + c) * 3;
            pos[o] = q[c * 3]; pos[o + 1] = q[c * 3 + 1]; pos[o + 2] = q[c * 3 + 2];
            col[o] = r; col[o + 1] = g; col[o + 2] = bl;
          }
          v += 4;
        }
      }
      n++;
    }
    this.geo.setDrawRange(0, n * SEGS * 2 * 6);
    this.geo.attributes.position.needsUpdate = true;
    this.geo.attributes.color.needsUpdate = true;
    this.mesh.visible = n > 0;

    while (this.knots.length < knots.length) {
      const m = new THREE.Mesh(this.knotGeo, new THREE.MeshBasicMaterial({ map: this.knotTex, side: THREE.DoubleSide, alphaTest: 0.1 }));
      this.group.add(m);
      this.knots.push(m);
    }
    this.knots.forEach((m, i) => {
      const k = knots[i];
      m.visible = !!k;
      if (!k) return;
      m.position.set(k.x + 0.5, k.y + 0.375, k.z + 0.5);
      const l = 0.3 + 0.7 * Math.max(0, Math.min(1, draw.light(k.x + 0.5, k.y + 0.5, k.z + 0.5)));
      (m.material as THREE.MeshBasicMaterial).color.setRGB(l, l, l);
    });
  }
}
